/* global trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      // A variant can have multiple cart lines when its line-item properties differ.
      // Track each unique line key instead of overwriting quantities by variant ID.
      this.currentQuantities = new Map();
      this.querySelectorAll('.cart-item').forEach((item) => {
        if (item.dataset.lineKey) {
          this.currentQuantities.set(item.dataset.lineKey, Number(item.querySelector('.qty-input__input').value));
        }
      });

      this.quantityTimers = new Map();
      this.quantityQueue = new Map();
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
    }

    disconnectedCallback() {
      this.quantityTimers?.forEach((timer) => clearTimeout(timer));
      this.quantityTimers?.clear();
      this.quantityQueue?.clear();
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const removeButton = evt.target.closest('.js-remove-item');
      if (!removeButton || !this.contains(removeButton)) return;
      evt.preventDefault();
      const key = removeButton.closest('.cart-item')?.dataset.lineKey;
      if (!key) return;
      clearTimeout(this.quantityTimers.get(key));
      this.quantityTimers.delete(key);
      this.queueQuantityUpdate(key, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (!evt.target.matches('.qty-input__input')) return;
      const key = evt.target.closest('.cart-item')?.dataset.lineKey;
      if (!key) return;
      const quantity = evt.target.value;
      const name = document.activeElement.name;
      clearTimeout(this.quantityTimers.get(key));
      this.quantityTimers.set(key, setTimeout(() => {
        this.quantityTimers.delete(key);
        this.queueQuantityUpdate(key, quantity, name);
      }, 300));
    }

    async queueQuantityUpdate(key, quantity, name) {
      this.quantityQueue.set(key, { quantity, name });
      if (this.processingQuantities) return;
      this.processingQuantities = true;
      try {
        while (this.quantityQueue.size && this.isConnected) {
          const [lineKey, change] = this.quantityQueue.entries().next().value;
          this.quantityQueue.delete(lineKey);
          await this.updateQuantity(lineKey, change.quantity, change.name);
        }
      } finally {
        this.processingQuantities = false;
      }
    }

    /**
     * Updates the quantity of a line item.
     * @param {string} lineKey - Line item key captured when the shopper edited it.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(lineKey, quantity, name) {
      const lineItem = Array.from(this.querySelectorAll('.cart-item'))
        .find((item) => item.dataset.lineKey === lineKey);
      if (!lineItem) return;
      const line = lineItem.id.replace('cart-item-', '');
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        id: lineKey,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const variantId = Number(lineItem.dataset.variantId);
        const oldQuantity = this.currentQuantities.get(lineKey) ?? Number(lineItem.querySelector('.qty-input__input').dataset.initialValue);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartContent = document.getElementById('cart-summary-and-blocks');
          if (cartContent) cartContent.hidden = true;
        }

        const cartMarkupMissing = this.renderSections(data.sections);

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: data,
              variantId,
              lineKey,
              oldQuantity,
              newQuantity: Number(quantity)
            }
          }));
        }

        // Cart keys can change after discounts or line merges. Refresh them from Shopify's
        // authoritative response, not from the original line index or requested quantity.
        this.currentQuantities = new Map(data.items.map((item) => [item.key, Number(item.quantity)]));

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }

        // The quantity changed but the response had no cart markup to show it: fetch the cart again
        if (cartMarkupMissing) {
          if (this.cartDrawer) {
            this.cartDrawer.refresh(true);
          } else {
            this.refresh();
          }
        }
      } catch (error) {
        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }
        console.log(error); // eslint-disable-line

        this.disableLoading();

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          const quantityInput = input.closest('quantity-input');
          if (quantityInput) quantityInput.currentQty = input.dataset.initialValue;
        }
      } finally {
        this.classList.remove('pointer-events-none');
        this.disableLoading();

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        // No cart markup came back: in the drawer, fall back to refetching the whole drawer
        if (this.renderSections(data) && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          },
          {
            id: 'cart-tiered-progress-wrapper',
            section: cartDrawerId,
            selector: '#cart-tiered-progress-wrapper'
          },
          {
            id: 'ast-cart-pack-wrapper',
            section: cartDrawerId,
            selector: '#ast-cart-pack-wrapper'
          }
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: document.getElementById('cart-summary')?.dataset?.section,
            selector: '.cart__summary'
          },
          {
            // AST: the sticker pack meter on /cart (sections/main-cart.liquid)
            id: 'ast-cart-pack-wrapper',
            section: document.getElementById('cart-summary')?.dataset?.section,
            selector: '#ast-cart-pack-wrapper'
          }
        ];
      }

      return sections;
    }

    /**
     * Swaps in the new markup for each part of the cart. A part the response has no markup for
     * (Shopify returns null for a section that failed to render) is left as it is. Emptying it
     * instead is what blanked the cart drawer after a quantity change.
     * @param {object} sections - Section HTML keyed by section id.
     * @returns {boolean} True if the cart items markup was missing from the response.
     */
    renderSections(sections) {
      let cartMarkupMissing = false;

      this.getSectionsToRender().forEach((section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl) return;

        const { selector } = section;
        const html = CartItems.getElementHTML(sections ? sections[section.section] : null, selector);
        if (html === null) {
          if (section.id === 'cart-items') cartMarkupMissing = true;
          return;
        }

        const el = sectionEl.querySelector(selector) || sectionEl;
        el.innerHTML = html;
      });

      return cartMarkupMissing;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {?string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {?string} The element's innerHTML, or null if there's no HTML or no such element.
     */
    static getElementHTML(html, selector) {
      if (!html) return null;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      // AST (Oct 26 2026): one ring for the whole cart, centred in the drawer body (or over the cart page's items),
      // in place of the theme's per-line loader that was positioned against the quantity row and landed on the title
      const busy = this.getBusyRing();
      if (busy) {
        const body = this.cartDrawer && this.cartDrawer.contains(this) ? this.cartDrawer.querySelector('.drawer__content') : null;
        if (body) {
          busy.style.top = `${body.offsetTop}px`;
          busy.style.height = `${body.offsetHeight}px`;
        }
        busy.hidden = false;
        // the ring takes its colours from the surface under its centre (QA Q1, Oct 27 2026): a surface that sets
        // --ast-busy-arc (the ink sticker pack panel) gets its own arc and track, anything else the default violet
        const ring = busy.querySelector('.ast-cart-busy__ring');
        if (ring) {
          ring.style.removeProperty('--ast-busy-arc');
          ring.style.removeProperty('--ast-busy-track');
          const r = ring.getBoundingClientRect();
          const under = document.elementsFromPoint(r.left + r.width / 2, r.top + r.height / 2).find((el) => !busy.contains(el));
          if (under) {
            const cs = getComputedStyle(under);
            const arc = cs.getPropertyValue('--ast-busy-arc').trim();
            if (arc) {
              ring.style.setProperty('--ast-busy-arc', arc);
              ring.style.setProperty('--ast-busy-track', cs.getPropertyValue('--ast-busy-track').trim() || 'rgb(255 255 255 / 0.2)');
            }
          }
        }
      }

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * The busy ring for this cart: the drawer's own when this list sits in the drawer, else the one inside the list.
     * @returns {?Element}
     */
    getBusyRing() {
      if (this.cartDrawer && this.cartDrawer.contains(this)) return this.cartDrawer.querySelector(':scope > .ast-cart-busy');
      return this.querySelector('.ast-cart-busy');
    }

    /**
     * Hides the busy ring.
     */
    disableLoading() {
      const busy = this.getBusyRing();
      if (busy) busy.hidden = true;
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}

